// src/utils/invoiceStorageService.js
// ============================================================================
// INVOICE STORAGE SERVICE — Abstraction for saving invoice PDFs
// ============================================================================
// Two distinct workflows:
//
//   A. SUPERVISOR / OPERATIONAL (per-invoice):
//      Download PDF locally → attach to email manually.
//      No shared-storage step. Fast and simple.
//
//   B. MONTH-END FINANCE / ARCHIVE (batch):
//      Collect tax invoices for a month → archive to shared storage
//      (SharePoint) → prepare accountant email → mark as archived.
//      This is the correct place for shared-folder saving.
//
// Shared folder structure (on SharePoint, when configured):
//   {baseFolderPath}/Invoices/
//     {Client Name}/
//       {Project Code}/
//         {Year}/
//           Tax/
//           Proforma/
//
// Storage backend is abstracted so the archive workflow works whether the
// SharePoint integration is live or still local-only (download ZIP).
// ============================================================================

import { supabase } from '../supabaseClient';

// ============================================================================
// PATH & FILENAME HELPERS
// ============================================================================

/**
 * Build the expected folder path for an invoice PDF.
 * @param {object} params
 * @param {string} params.clientName
 * @param {string} params.projectCode
 * @param {string} params.invoiceDate - YYYY-MM-DD
 * @param {string} params.invoiceDocType - 'tax' | 'proforma'
 * @returns {string} Folder path like "Invoices/Client/ProjectCode/2026/Tax"
 */
export function buildInvoiceFolderPath({ clientName, projectCode, invoiceDate, invoiceDocType }) {
  const year = invoiceDate ? invoiceDate.slice(0, 4) : new Date().getFullYear().toString();
  const typeFolder = invoiceDocType === 'proforma' ? 'Proforma' : 'Tax';
  const safeName = (clientName || 'Unknown Client').replace(/[/\\:*?"<>|]/g, '_');
  const safeCode = (projectCode || 'Unknown').replace(/[/\\:*?"<>|]/g, '_');
  return `Invoices/${safeName}/${safeCode}/${year}/${typeFolder}`;
}

/**
 * Build a filename for an invoice PDF.
 * @param {object} params
 * @param {string} params.officeKey - 'rotterdam' | 'mumbai'
 * @param {string} params.invoiceDocType - 'tax' | 'proforma'
 * @param {string} params.invoiceNumber
 * @param {string} params.projectName
 * @returns {string}
 */
export function buildInvoiceFilename({ officeKey, invoiceDocType, invoiceNumber, projectName }) {
  const typeTag = invoiceDocType === 'proforma' ? 'PROFORMA' : 'INVOICE';
  const projSlug = (projectName || 'project')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .substring(0, 30);
  return `BRIO_${(officeKey || 'NL').toUpperCase()}_${typeTag}_${invoiceNumber || 'DRAFT'}_${projSlug}.pdf`;
}

// ============================================================================
// WORKFLOW A: SUPERVISOR LOCAL DOWNLOAD
// ============================================================================

/**
 * Download a blob to the user's local filesystem.
 * On iOS/iPadOS Safari the `download` attribute on blob URLs is not supported
 * and causes the current page to navigate to the PDF (full-screen takeover).
 * We detect this and open in a new tab instead so the app stays intact.
 * @param {Blob} blob
 * @param {string} filename
 */
export function downloadLocal(blob, filename) {
  if (!blob) return;
  const url = URL.createObjectURL(blob);

  // iOS/iPadOS Safari does not support a.download for blob URLs —
  // it navigates the current tab, destroying app state.
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isIOS) {
    // Open in a new tab — user can share/save from Safari's share sheet
    window.open(url, '_blank');
    // Revoke after a longer delay so the new tab has time to load
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ============================================================================
// WORKFLOW B: MONTH-END ARCHIVE (batch operations)
// ============================================================================

/**
 * Convert a Blob to a base64 string.
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Check if SharePoint storage is configured.
 * Reads the sharepointStorage key from app_settings.
 * @returns {Promise<{ configured: boolean, siteName?: string, libraryName?: string }>}
 */
export async function isSharePointConfigured() {
  try {
    const { data, error } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'sharepointStorage')
      .maybeSingle();

    if (error || !data) return { configured: false };

    const config = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
    const configured = !!(config?.siteId && config?.driveId);
    return {
      configured,
      siteName: config?.siteName || '',
      libraryName: config?.libraryName || '',
    };
  } catch {
    return { configured: false };
  }
}

/**
 * Upload a single invoice PDF to SharePoint via Edge Function.
 * Used by the month-end archive workflow, NOT the daily supervisor workflow.
 *
 * @param {Blob} blob - The PDF blob to upload
 * @param {string} filename - Desired filename
 * @param {string} folderPath - Destination folder (from buildInvoiceFolderPath)
 * @param {number|string} invoiceId - The invoice record ID
 * @returns {Promise<{ success: boolean, error?: string, webUrl?: string, storagePath?: string }>}
 */
export async function uploadToSharedStorage(blob, filename, folderPath, invoiceId) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return { success: false, error: 'Not authenticated. Please sign in again.' };
    }

    const fileBase64 = await blobToBase64(blob);

    // Derive Supabase project URL
    const { data: { publicUrl } } = supabase.storage.from('dummy').getPublicUrl('');
    const supabaseUrl = publicUrl.replace(/\/storage\/v1\/object\/public\/dummy\/$/, '');

    const response = await fetch(`${supabaseUrl}/functions/v1/upload-invoice-sharepoint`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ invoiceId, filename, folderPath, fileBase64 }),
    });

    const result = await response.json();

    if (!response.ok || !result.ok) {
      return { success: false, error: result.error || `Upload failed (${response.status})` };
    }

    return { success: true, webUrl: result.webUrl, storagePath: result.storagePath };
  } catch (err) {
    console.error('[invoiceStorage] SharePoint upload error:', err);
    return { success: false, error: err?.message || 'Failed to upload to SharePoint.' };
  }
}

/**
 * Archive a batch of tax invoices for a given month.
 * This is the main entry point for the month-end finance workflow.
 *
 * For each invoice:
 *   1. Generate PDF (if not cached)
 *   2. Upload to SharePoint (if configured)
 *   3. Mark invoice as archived
 *
 * @param {object} params
 * @param {Array<object>} params.invoices - Invoice records to archive
 * @param {function} params.generatePdf - async (invoiceId, officeKey, docType) => Blob|null
 * @param {function} params.onProgress - (index, total, invoiceNumber, status) => void
 * @param {object} params.projectsMap - Map of projectId → project (with clientName, projectCode, etc.)
 * @param {boolean} params.uploadToSharePoint - Whether to upload to SharePoint (vs local-only)
 * @param {number} params.archivedByUserId - User ID to record as archiver
 * @returns {Promise<{ archived: number, failed: number, errors: string[] }>}
 */
export async function archiveMonthlyTaxInvoices({
  invoices,
  generatePdf,
  onProgress,
  projectsMap,
  uploadToSharePoint = false,
  archivedByUserId,
}) {
  let archived = 0;
  let failed = 0;
  const errors = [];
  const total = invoices.length;

  for (let i = 0; i < invoices.length; i++) {
    const inv = invoices[i];
    const invLabel = inv.invoiceNumber || `#${inv.id}`;
    onProgress?.(i, total, invLabel, 'generating');

    try {
      // 1. Generate PDF
      const officeKey = (inv.officeLocation || 'NL').toLowerCase() === 'in' ? 'mumbai' : 'rotterdam';
      const blob = await generatePdf(inv.id, officeKey, inv.invoiceDocType || 'tax');

      if (!blob) {
        errors.push(`${invLabel}: PDF generation failed`);
        failed++;
        onProgress?.(i, total, invLabel, 'error');
        continue;
      }

      // 2. Build filename + folder path
      const project = projectsMap?.[inv.projectId] || {};
      const filename = buildInvoiceFilename({
        officeKey,
        invoiceDocType: inv.invoiceDocType || 'tax',
        invoiceNumber: inv.invoiceNumber,
        projectName: project.name,
      });
      const folderPath = buildInvoiceFolderPath({
        clientName: project.clientName || 'Client',
        projectCode: project.projectCode || project.name || 'Project',
        invoiceDate: inv.invoiceDate,
        invoiceDocType: inv.invoiceDocType || 'tax',
      });

      // 3. Upload to SharePoint if enabled and configured
      let uploadResult = null;
      if (uploadToSharePoint) {
        onProgress?.(i, total, invLabel, 'uploading');
        uploadResult = await uploadToSharedStorage(blob, filename, folderPath, inv.id);
        if (!uploadResult.success) {
          errors.push(`${invLabel}: ${uploadResult.error}`);
          failed++;
          onProgress?.(i, total, invLabel, 'error');
          continue;
        }
      }

      // 4. Mark invoice as archived in DB
      const month = inv.invoiceDate ? inv.invoiceDate.slice(0, 7) : null;
      const { error: updateErr } = await supabase
        .from('invoices')
        .update({
          archived_to_storage: !!uploadResult?.success,
          archived_at: new Date().toISOString(),
          archived_by: archivedByUserId || null,
          archive_batch_month: month,
          pdf_generated_at: new Date().toISOString(),
          pdf_filename: filename,
        })
        .eq('id', inv.id);

      if (updateErr) {
        errors.push(`${invLabel}: DB update failed — ${updateErr.message}`);
        failed++;
        onProgress?.(i, total, invLabel, 'error');
        continue;
      }

      archived++;
      onProgress?.(i, total, invLabel, 'done');
    } catch (err) {
      errors.push(`${invLabel}: ${err?.message || 'Unknown error'}`);
      failed++;
      onProgress?.(i, total, invLabel, 'error');
    }
  }

  return { archived, failed, errors };
}

/**
 * Mark invoices as sent to accountant.
 * Called after the admin prepares/sends the accountant email.
 *
 * @param {Array<number>} invoiceIds - IDs of invoices to mark
 * @param {number} sentByUserId - User ID who triggered the send
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function markSentToAccountant(invoiceIds, sentByUserId) {
  try {
    const { error } = await supabase
      .from('invoices')
      .update({
        sent_to_accountant: true,
        sent_to_accountant_at: new Date().toISOString(),
        sent_to_accountant_by: sentByUserId || null,
      })
      .in('id', invoiceIds);

    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (err) {
    return { success: false, error: err?.message || 'Failed to update invoices.' };
  }
}

/**
 * Fetch files previously uploaded for an invoice.
 * @param {number|string} invoiceId
 * @returns {Promise<Array<{ id, storagePath, filename, webUrl, uploadedAt }>>}
 */
export async function getInvoiceFiles(invoiceId) {
  try {
    const { data, error } = await supabase
      .from('invoice_files')
      .select('id, storage_path, filename, sp_web_url, uploaded_at, uploaded_by, file_size')
      .eq('invoice_id', invoiceId)
      .order('uploaded_at', { ascending: false });

    if (error) return [];
    return (data || []).map(f => ({
      id: f.id,
      storagePath: f.storage_path,
      filename: f.filename,
      webUrl: f.sp_web_url,
      uploadedAt: f.uploaded_at,
      uploadedBy: f.uploaded_by,
      fileSize: f.file_size,
    }));
  } catch {
    return [];
  }
}

/**
 * Build a summary of invoices for accountant email body.
 * @param {Array<object>} invoices - Invoice records for the month
 * @param {string} month - e.g. "2026-02"
 * @param {function} fmtCurr - Currency formatter
 * @returns {{ subject: string, body: string, invoiceCount: number, totalAmount: number }}
 */
export function buildAccountantEmailSummary(invoices, month, fmtCurr) {
  const taxInvoices = invoices.filter(i => (i.invoiceDocType || i.invoice_doc_type) !== 'proforma');
  const totalAmount = taxInvoices.reduce((sum, i) => sum + (Number(i.netAmount || i.net_amount) || 0), 0);

  const monthLabel = month
    ? new Date(month + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : 'Previous Month';

  const subject = `Tax Invoices — ${monthLabel}`;

  const lines = taxInvoices.map(inv => {
    const num = inv.invoiceNumber || inv.invoice_number || `#${inv.id}`;
    const amt = fmtCurr ? fmtCurr(inv.netAmount || inv.net_amount) : `${inv.netAmount || inv.net_amount}`;
    const dt = inv.invoiceDate || inv.invoice_date || '';
    return `  ${num}  |  ${dt}  |  ${amt}`;
  });

  const body = [
    `Dear CA,`,
    ``,
    `Please find attached the tax invoices for ${monthLabel}.`,
    ``,
    `Summary:`,
    `  Invoices: ${taxInvoices.length}`,
    `  Total net amount: ${fmtCurr ? fmtCurr(totalAmount) : totalAmount}`,
    ``,
    `Invoice list:`,
    ...lines,
    ``,
    `Please confirm receipt.`,
    ``,
    `Best regards,`,
    `Architecture BRIO`,
  ].join('\n');

  return { subject, body, invoiceCount: taxInvoices.length, totalAmount };
}
